import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FileText, Folder, Plus, Search, Download, Archive, Star } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

interface Document {
  id: string;
  title: string;
  description: string;
  category: string;
  type: string;
  file_url: string;
  file_name: string;
  file_size: number;
  tags: string[];
  importance: 'low' | 'medium' | 'high' | 'critical';
  created_by: string;
  created_at: string;
  updated_at: string;
  access_level: 'public' | 'internal' | 'confidential' | 'restricted';
  retention_period: number; // in years
  is_archived: boolean;
}

interface Category {
  id: string;
  name: string;
  description: string;
  color: string;
  document_count: number;
}

export default function DocumentArchivePage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [importanceFilter, setImportanceFilter] = useState("all");
  const [isNewDocumentOpen, setIsNewDocumentOpen] = useState(false);
  const [isNewCategoryOpen, setIsNewCategoryOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [newDocument, setNewDocument] = useState({
    title: "",
    description: "",
    category: "",
    type: "",
    tags: "",
    importance: "medium",
    access_level: "internal",
    retention_period: 7
  });
  const [newCategory, setNewCategory] = useState({
    name: "",
    description: "",
    color: "#6366f1"
  });

  const queryClient = useQueryClient();

  const { data: documents = [], isLoading } = useQuery({
    queryKey: ['archived-documents'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('archived_documents' as any)
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return (data as unknown as Document[]) || [];
    }
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['document-categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('document_categories' as any)
        .select('*')
        .order('name');
      
      if (error) throw error;
      return (data as unknown as Category[]) || [];
    }
  });

  const createDocumentMutation = useMutation({
    mutationFn: async (documentData: any) => {
      let file_url = "";
      let file_name = "";
      let file_size = 0;

      if (selectedFile) {
        const fileExt = selectedFile.name.split('.').pop();
        const fileName = `${Date.now()}.${fileExt}`;
        
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('client-attachments')
          .upload(`documents/${fileName}`, selectedFile);

        if (uploadError) throw uploadError;
        
        file_url = uploadData.path;
        file_name = selectedFile.name;
        file_size = selectedFile.size;
      }

      const { data, error } = await supabase
        .from('archived_documents' as any)
        .insert([{
          ...documentData,
          file_url,
          file_name,
          file_size,
          tags: documentData.tags.split(',').map((tag: string) => tag.trim()).filter(Boolean)
        }])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['archived-documents'] });
      setIsNewDocumentOpen(false);
      setSelectedFile(null);
      setNewDocument({
        title: "", description: "", category: "", type: "", tags: "",
        importance: "medium", access_level: "internal", retention_period: 7
      });
      toast.success("تم حفظ الوثيقة بنجاح");
    }
  });

  const createCategoryMutation = useMutation({
    mutationFn: async (category: any) => {
      const { data, error } = await supabase
        .from('document_categories' as any)
        .insert([{ ...category, document_count: 0 }])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-categories'] });
      setIsNewCategoryOpen(false);
      setNewCategory({ name: "", description: "", color: "#6366f1" });
      toast.success("تم إنشاء الفئة بنجاح");
    }
  });

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = categoryFilter === "all" || doc.category === categoryFilter;
    const matchesImportance = importanceFilter === "all" || doc.importance === importanceFilter;
    return matchesSearch && matchesCategory && matchesImportance;
  });

  const getImportanceColor = (importance: string) => {
    switch (importance) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getAccessLevelColor = (level: string) => {
    switch (level) {
      case 'public': return 'bg-blue-100 text-blue-800';
      case 'internal': return 'bg-green-100 text-green-800';
      case 'confidential': return 'bg-yellow-100 text-yellow-800';
      case 'restricted': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      if (!newDocument.title) {
        setNewDocument({ ...newDocument, title: file.name.split('.')[0] });
      }
    }
  };

  const handleDownload = async (document: Document) => {
    if (!document.file_url) return;
    
    const { data, error } = await supabase.storage
      .from('client-attachments')
      .download(document.file_url);
    
    if (error) {
      toast.error("خطأ في تحميل الملف");
      return;
    }
    
    const url = URL.createObjectURL(data);
    const link = window.document.createElement('a');
    link.href = url;
    link.download = document.file_name;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="container mx-auto p-4 md:p-6 max-w-7xl">
      <div className="flex items-center gap-4 mb-8">
        <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center shadow-lg">
          <Archive className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">أرشيف المستندات</h1>
          <p className="text-muted-foreground mt-1">
            حفظ الوثائق الهامة وإدارة الأرشيف
          </p>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">إجمالي الوثائق</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{documents.length}</div>
            <p className="text-xs text-muted-foreground">وثيقة محفوظة</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">الفئات</CardTitle>
            <Folder className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{categories.length}</div>
            <p className="text-xs text-muted-foreground">فئة نشطة</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">وثائق حرجة</CardTitle>
            <Star className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {documents.filter(d => d.importance === 'critical').length}
            </div>
            <p className="text-xs text-muted-foreground">عالية الأهمية</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">حجم الأرشيف</CardTitle>
            <Archive className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatFileSize(documents.reduce((sum, doc) => sum + doc.file_size, 0))}
            </div>
            <p className="text-xs text-muted-foreground">إجمالي</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="documents" className="space-y-6">
        <TabsList>
          <TabsTrigger value="documents">الوثائق</TabsTrigger>
          <TabsTrigger value="categories">الفئات</TabsTrigger>
        </TabsList>

        <TabsContent value="documents">
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="البحث في الوثائق..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pr-10"
              />
            </div>
            
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="تصفية حسب الفئة" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الفئات</SelectItem>
                {categories.map(category => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={importanceFilter} onValueChange={setImportanceFilter}>
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="تصفية حسب الأهمية" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع المستويات</SelectItem>
                <SelectItem value="critical">حرجة</SelectItem>
                <SelectItem value="high">عالية</SelectItem>
                <SelectItem value="medium">متوسطة</SelectItem>
                <SelectItem value="low">منخفضة</SelectItem>
              </SelectContent>
            </Select>

            <Dialog open={isNewDocumentOpen} onOpenChange={setIsNewDocumentOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="w-4 h-4 ml-2" />
                  وثيقة جديدة
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>إضافة وثيقة جديدة</DialogTitle>
                  <DialogDescription>
                    ارفع وثيقة جديدة إلى الأرشيف
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">رفع الملف</label>
                    <input
                      type="file"
                      onChange={handleFileSelect}
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                      className="w-full p-2 border rounded-md"
                    />
                    {selectedFile && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {selectedFile.name} ({formatFileSize(selectedFile.size)})
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block">عنوان الوثيقة *</label>
                    <Input
                      value={newDocument.title}
                      onChange={(e) => setNewDocument({ ...newDocument, title: e.target.value })}
                      placeholder="أدخل عنوان الوثيقة"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">الفئة</label>
                      <Select value={newDocument.category} onValueChange={(value) => setNewDocument({ ...newDocument, category: value })}>
                        <SelectTrigger>
                          <SelectValue placeholder="اختر الفئة" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map(category => (
                            <SelectItem key={category.id} value={category.id}>
                              {category.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="text-sm font-medium mb-2 block">نوع الوثيقة</label>
                      <Select value={newDocument.type} onValueChange={(value) => setNewDocument({ ...newDocument, type: value })}>
                        <SelectTrigger>
                          <SelectValue placeholder="اختر النوع" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="contract">عقد</SelectItem>
                          <SelectItem value="license">ترخيص</SelectItem>
                          <SelectItem value="certificate">شهادة</SelectItem>
                          <SelectItem value="policy">سياسة</SelectItem>
                          <SelectItem value="procedure">إجراء</SelectItem>
                          <SelectItem value="report">تقرير</SelectItem>
                          <SelectItem value="other">أخرى</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">الأهمية</label>
                      <Select value={newDocument.importance} onValueChange={(value) => setNewDocument({ ...newDocument, importance: value })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">منخفضة</SelectItem>
                          <SelectItem value="medium">متوسطة</SelectItem>
                          <SelectItem value="high">عالية</SelectItem>
                          <SelectItem value="critical">حرجة</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="text-sm font-medium mb-2 block">مستوى الوصول</label>
                      <Select value={newDocument.access_level} onValueChange={(value) => setNewDocument({ ...newDocument, access_level: value })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="public">عام</SelectItem>
                          <SelectItem value="internal">داخلي</SelectItem>
                          <SelectItem value="confidential">سري</SelectItem>
                          <SelectItem value="restricted">محدود</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block">فترة الاحتفاظ (سنوات)</label>
                    <Input
                      type="number"
                      value={newDocument.retention_period}
                      onChange={(e) => setNewDocument({ ...newDocument, retention_period: parseInt(e.target.value) })}
                      min="1"
                      max="50"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block">الكلمات المفتاحية</label>
                    <Input
                      value={newDocument.tags}
                      onChange={(e) => setNewDocument({ ...newDocument, tags: e.target.value })}
                      placeholder="أدخل الكلمات مفصولة بفواصل"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block">الوصف</label>
                    <Textarea
                      value={newDocument.description}
                      onChange={(e) => setNewDocument({ ...newDocument, description: e.target.value })}
                      placeholder="وصف مختصر للوثيقة"
                      rows={3}
                    />
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button 
                      onClick={() => createDocumentMutation.mutate(newDocument)}
                      disabled={!newDocument.title || createDocumentMutation.isPending}
                    >
                      حفظ الوثيقة
                    </Button>
                    <Button variant="outline" onClick={() => setIsNewDocumentOpen(false)}>
                      إلغاء
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {isLoading ? (
            <div className="text-center py-8">جاري التحميل...</div>
          ) : (
            <div className="grid gap-4">
              {filteredDocuments.map((document) => (
                <Card key={document.id}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-lg mb-1">{document.title}</CardTitle>
                        <CardDescription>{document.description}</CardDescription>
                        <div className="flex flex-wrap gap-2 mt-2">
                          <Badge className={getImportanceColor(document.importance)}>
                            {document.importance === 'critical' && 'حرجة'}
                            {document.importance === 'high' && 'عالية'}
                            {document.importance === 'medium' && 'متوسطة'}
                            {document.importance === 'low' && 'منخفضة'}
                          </Badge>
                          <Badge className={getAccessLevelColor(document.access_level)}>
                            {document.access_level === 'public' && 'عام'}
                            {document.access_level === 'internal' && 'داخلي'}
                            {document.access_level === 'confidential' && 'سري'}
                            {document.access_level === 'restricted' && 'محدود'}
                          </Badge>
                          {document.type && (
                            <Badge variant="outline">
                              {document.type === 'contract' && 'عقد'}
                              {document.type === 'license' && 'ترخيص'}
                              {document.type === 'certificate' && 'شهادة'}
                              {document.type === 'policy' && 'سياسة'}
                              {document.type === 'procedure' && 'إجراء'}
                              {document.type === 'report' && 'تقرير'}
                              {document.type === 'other' && 'أخرى'}
                            </Badge>
                          )}
                        </div>
                        {document.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {document.tags.map((tag, index) => (
                              <Badge key={index} variant="secondary" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="text-left">
                        <div className="text-sm text-muted-foreground">
                          {new Date(document.created_at).toLocaleDateString('ar-SA')}
                        </div>
                        {document.file_size > 0 && (
                          <div className="text-sm text-muted-foreground">
                            {formatFileSize(document.file_size)}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-muted-foreground">
                        الاحتفاظ: {document.retention_period} سنوات
                      </div>
                      {document.file_url && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleDownload(document)}
                        >
                          <Download className="w-4 h-4 ml-2" />
                          تحميل
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {filteredDocuments.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  لا توجد وثائق مطابقة للبحث
                </div>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="categories">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">فئات الوثائق</h2>
            <Dialog open={isNewCategoryOpen} onOpenChange={setIsNewCategoryOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="w-4 h-4 ml-2" />
                  فئة جديدة
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>إنشاء فئة جديدة</DialogTitle>
                  <DialogDescription>
                    أدخل تفاصيل الفئة الجديدة للوثائق
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">اسم الفئة *</label>
                    <Input
                      value={newCategory.name}
                      onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                      placeholder="أدخل اسم الفئة"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block">اللون</label>
                    <Input
                      type="color"
                      value={newCategory.color}
                      onChange={(e) => setNewCategory({ ...newCategory, color: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block">الوصف</label>
                    <Textarea
                      value={newCategory.description}
                      onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                      placeholder="وصف الفئة ونوع الوثائق التي تحتويها"
                      rows={3}
                    />
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button 
                      onClick={() => createCategoryMutation.mutate(newCategory)}
                      disabled={!newCategory.name || createCategoryMutation.isPending}
                    >
                      إنشاء الفئة
                    </Button>
                    <Button variant="outline" onClick={() => setIsNewCategoryOpen(false)}>
                      إلغاء
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((category) => (
              <Card key={category.id}>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: category.color }}
                    />
                    <div className="flex-1">
                      <CardTitle className="text-lg">{category.name}</CardTitle>
                      <CardDescription>{category.description}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-sm text-muted-foreground">
                    {category.document_count} وثيقة
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}